/**
 * CustomersList Component
 *
 * List view for customers/users across all tenants with:
 * - Pagination and sorting
 * - Search by name/email/company
 * - Filters by plan, status, silo mode
 * - Drill-down to customer detail with teams, jobs, subscriptions
 * - Admin impersonation ("View as customer")
 *
 * Uses service-role queries to bypass RLS.
 *
 * ------------------------------------------------------------------
 * Migrated for "Implement RTK Query hooks and caching for all API
 * endpoints": previously fetched via a hand-rolled `useEffect` + local
 * `useState`, and rendered hard-coded mock `AdminCustomerInfo[]` with
 * client-side search/filter/pagination behind a `// TODO: Call GET
 * /api/admin/customers with filters` comment. Now:
 * - Data fetching: `useAdminCustomers()` (`@/lib/api/apiClient`) — a
 *   TanStack Query hook, Zod-validated response, server-side
 *   search/filter/pagination via real query params.
 * - Filter/search/pagination state: `useApiStore` (`@/stores/apiStore`,
 *   Zustand) instead of five separate `useState` calls — the query key
 *   passed to `useAdminCustomers` is derived directly from the store, so
 *   changing a filter automatically refetches (same pattern as
 *   `app/routes/admin/Subscriptions.tsx`).
 * - Loading state: ShadCN `Skeleton`.
 * - Error state: `sonner` `toast.error` + inline retry.
 * - Impersonation: "Impersonate" button calls `useStartImpersonation()`
 *   (TanStack mutation); on success the session is written to
 *   `useApiStore`'s Zustand state and the admin customers cache is
 *   invalidated (see `useStartImpersonation`'s doc-comment).
 *
 * ------------------------------------------------------------------
 * GitHub issue #500 ("E2E: Admin leads — voice leads list, filters,
 * detail view") is covered against *this* page in `e2e/admin-leads.spec.ts`,
 * not a dedicated `/admin/leads` route — no "lead" concept (voice-widget
 * or otherwise) exists anywhere in this codebase to test. This is the
 * real, closest-existing admin list page. See that spec's doc-comment
 * and `docs/flows/10-admin-leads.md` / `docs/flows/GATE-113-SIGNOFF.md`
 * for the full rescoping rationale, and note the "View" button below
 * navigates to a route (`/admin/customers/:uuid`) that doesn't exist in
 * `router.tsx` yet (falls through to the catch-all redirect) — the E2E
 * spec deliberately does not assert that navigation works.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAdminCustomers, useStartImpersonation, reportApiError } from '@/lib/api/apiClient';
import { useApiStore } from '@/stores/apiStore';
import { Skeleton } from '@/components/ui/skeleton';
import type { AdminCustomersQueryParams } from '@/lib/validation/admin';

export default function CustomersList() {
  const navigate = useNavigate();

  const filters = useApiStore((state) => state.adminCustomersFilters);
  const setFilters = useApiStore((state) => state.setAdminCustomersFilters);

  const { data, isLoading, isError, error, refetch, isFetching } = useAdminCustomers(filters);
  const startImpersonation = useStartImpersonation();

  useEffect(() => {
    if (isError) {
      reportApiError(error, 'Failed to load customers');
    }
  }, [isError, error]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 space-y-4" aria-busy="true">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md">
          <div className="text-lg font-semibold text-destructive">Error Loading Customers</div>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'Failed to fetch customers'}
          </p>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="mt-4 px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasMore = filters.offset + filters.limit < data.total_count;
  const hasPrev = filters.offset > 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold">Customers</h1>
            <button
              onClick={() => navigate('/admin')}
              className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium"
            >
              Back to Dashboard
            </button>
          </div>

          {/* Filters and Search */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-4">
            <input
              type="text"
              placeholder="Search by name, email, or company..."
              value={filters.search}
              onChange={(e) => setFilters({ search: e.target.value, offset: 0 })}
              className="px-3 py-2 border border-border rounded-md text-sm"
            />

            <select
              value={filters.plan}
              onChange={(e) =>
                setFilters({
                  plan: e.target.value as AdminCustomersQueryParams['plan'],
                  offset: 0,
                })
              }
              className="px-3 py-2 border border-border rounded-md text-sm"
            >
              <option value="">All Plans</option>
              <option value="STARTER">Starter</option>
              <option value="PROFESSIONAL">Professional</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>

            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({
                  status: e.target.value as AdminCustomersQueryParams['status'],
                  offset: 0,
                })
              }
              className="px-3 py-2 border border-border rounded-md text-sm"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>

            <div className="text-sm text-muted-foreground pt-2">
              Showing {data.data.length} of {data.total_count} customers
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {data.data.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No customers found</p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto border border-border rounded-lg mb-4">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">Name</th>
                    <th className="px-4 py-2 text-left font-semibold">Email</th>
                    <th className="px-4 py-2 text-left font-semibold">Company</th>
                    <th className="px-4 py-2 text-left font-semibold">Plan</th>
                    <th className="px-4 py-2 text-left font-semibold">Status</th>
                    <th className="px-4 py-2 text-left font-semibold">Last Login</th>
                    <th className="px-4 py-2 text-left font-semibold">Created</th>
                    <th className="px-4 py-2 text-left font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((customer) => (
                    <tr
                      key={customer.uuid}
                      data-testid="admin-customer-row"
                      className="border-b border-border hover:bg-muted/50"
                    >
                      <td className="px-4 py-2">{customer.name || '—'}</td>
                      <td className="px-4 py-2 text-xs font-mono">{customer.email}</td>
                      <td className="px-4 py-2">{customer.company_name || '—'}</td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-muted">
                          {customer.plan}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            customer.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : customer.status === 'trial'
                                ? 'bg-blue-100 text-blue-800'
                                : customer.status === 'inactive'
                                  ? 'bg-gray-100 text-gray-800'
                                  : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {customer.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {customer.last_login
                          ? new Date(customer.last_login).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              navigate(`/admin/customers/${customer.uuid}`, {
                                state: { customer },
                              })
                            }
                            className="text-xs px-2 py-1 border border-border rounded hover:bg-accent"
                          >
                            View
                          </button>
                          <button
                            onClick={() =>
                              startImpersonation.mutate({ impersonatedUserId: customer.uuid })
                            }
                            disabled={startImpersonation.isPending}
                            className="text-xs px-2 py-1 border border-border rounded hover:bg-accent disabled:opacity-50"
                          >
                            {startImpersonation.isPending ? 'Starting...' : 'Impersonate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center">
              <button
                onClick={() => setFilters({ offset: Math.max(0, filters.offset - filters.limit) })}
                disabled={!hasPrev}
                className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>

              <div className="text-sm text-muted-foreground">
                Page {Math.floor(filters.offset / filters.limit) + 1}
              </div>

              <button
                onClick={() => setFilters({ offset: filters.offset + filters.limit })}
                disabled={!hasMore}
                className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
